import React, { useState } from "react"
import { getCountryName } from "helpers/countries"
import dayjs from "dayjs"
import DineroFactory from "dinero.js"
import {
  CustomReasonCode,
  DeliveryShippingFee,
  Order,
  Reason
} from "lib/types/generated/graphql-types"
import { useQuery } from "@apollo/client"

import GET_STORE_GROUP from "../../../../../graphql/queries/store/GetStoreGroup"

import { Header } from "../Shared.styled"
import { Blur, ReasonWrapper, Summary } from "./OrderSummary.styled"

import Money from "../../../../Money"
import { Item, Label, Value } from "../../../../Ui/List/List"
import FlagIcon from "../../../../Ui/FlagIcon"
import DotLoader from "../../../../Ui/DotLoader"
import CopyButton from "../../../../Ui/CopyButton"
import { VariantRow } from "./VariantRow"
import { GiftRow } from "./GiftRow"
import { ShippingRow } from "./ShippingRow"
import { NoticeBanner } from "../../../../Ui/Messages/NoticeBanner"
import { Deliveries } from "../../../../../classes/order/Deliveries"
import { GiftCardProductRow } from "./GiftCardProductRow"
import { ReasonType } from "lib/types/order"
import ReasonInput from "../../Reason/ReasonInput"
import { Refunds } from "../../../../../classes/order/Refunds"

type Props = {
  order: Order
  refetch: () => void
  orderLoading: boolean
  customReasons: CustomReasonCode[]
}

export const OrderSummary = ({ order, refetch, customReasons }: Props) => {
  const [reason, setReason] = useState<Reason>({ code: "", cause: "" })
  const [reasonType, setReasonType] = useState<ReasonType>()

  const { data, loading } = useQuery(GET_STORE_GROUP, {
    fetchPolicy: "cache-first",
    variables: { id: order?.storeGroupId }
  })
  const deliveries = new Deliveries(order.Deliveries)
  const refunds = new Refunds(order.Refunds)

  const orderLinesInDeliveries = deliveries
    .completedDeliveries()
    .flatMap((delivery) => delivery.orderLines)

  const giftCardProductsInDeliveries = deliveries
    .completedDeliveries()
    .flatMap((delivery) => delivery.giftCardProducts)

  const giftsInDeliveries = deliveries.completedDeliveries().flatMap((delivery) => delivery.gifts)

  const shippingFeesInDeliveries = order?.Deliveries?.filter(
    (delivery) => !!delivery.completed
  ).flatMap((delivery) => delivery.shippingFees)

  const getOrderShippingFee = (deliveryFee: DeliveryShippingFee) =>
    order?.shippingFees?.find((shippingFee) => shippingFee?.id === deliveryFee?.shippingFeeId)

  const reasonNotSet = (!reason.cause || !reason.code) && reasonType !== ReasonType.SKIP

  if (!order)
    return (
      <Blur>
        <Header>
          <h2>Order: #1234567</h2>
        </Header>
        <Summary>
          <Item flexibleHeight>
            <Label>E-mail:</Label>
            <Value>
              <>
                john.doe@email.com
                <CopyButton string={""} />
              </>
            </Value>
          </Item>
          <Item>
            <Label>Store group:</Label>
            <Value>store group</Value>
          </Item>
          <Item>
            <Label>Store market:</Label>
            <Value flex>
              <>Sweden</>
            </Value>
          </Item>
          <Item>
            <Label>Order date:</Label>
            <Value>2000-00-00 00:00</Value>
          </Item>
          <Item>
            <Label>Total order value:</Label>
            <Value>SEK 200.00</Value>
          </Item>
        </Summary>
      </Blur>
    )
  if (!order) return null
  return (
    <>
      <Header>
        <h2>Order: #{order?.reference}</h2>
      </Header>
      <Summary>
        <Item flexibleHeight>
          <Label>E-mail:</Label>
          <Value>
            <>
              {order?.billingAddress?.email}
              <CopyButton string={order?.billingAddress?.email} />
            </>
          </Value>
        </Item>
        <Item>
          <Label>Store group:</Label>
          <Value>{loading ? <DotLoader /> : data?.getStoreGroup?.name}</Value>
        </Item>
        <Item>
          <Label>Store market:</Label>
          <Value flex>
            <>
              <FlagIcon countryCode={order?.countryCode} />
              {getCountryName(order?.countryCode)}
            </>
          </Value>
        </Item>
        <Item>
          <Label>Order date:</Label>
          <Value>{dayjs(order?.date).format("YYYY-MM-DD HH:mm")}</Value>
        </Item>
        <Item>
          <Label>Total order value:</Label>
          <Value>
            <Money
              amount={parseInt(`${order?.totals?.grandTotal ?? 0}`)}
              currencyUnit={order?.currencyCode as DineroFactory.Currency}
            />
          </Value>
        </Item>
        {!refunds.pending() && (
          <ReasonWrapper>
            <Label>Reason:</Label>
            <ReasonInput
              reason={reason}
              setReason={setReason}
              reasonType={reasonType}
              setReasonType={setReasonType}
              customReasons={customReasons}
            />
          </ReasonWrapper>
        )}
      </Summary>
      {(!reasonNotSet || refunds.pending()) && (
        <>
          <h2>Order lines ({orderLinesInDeliveries?.length ?? 0})</h2>
          {orderLinesInDeliveries?.map((orderLine) => (
            <VariantRow
              key={orderLine?.orderLineId}
              orderLine={orderLine}
              order={order}
              refetch={refetch}
              currencyCode={order?.currencyCode}
              refunds={order?.Refunds}
              reason={reason}
            />
          ))}
          {orderLinesInDeliveries?.length === 0 && (
            <NoticeBanner type="important">
              Missing completed deliveries for orderlines
            </NoticeBanner>
          )}

          {giftsInDeliveries?.length > 0 && (
            <>
              <h2>Order gifts ({giftsInDeliveries?.length ?? 0})</h2>
              {giftsInDeliveries?.map((gift) => (
                <GiftRow
                  key={gift?.giftId}
                  deliveryGift={gift}
                  order={order}
                  refetch={refetch}
                  currencyCode={order?.currencyCode}
                  reason={reason}
                />
              ))}
            </>
          )}
          {giftCardProductsInDeliveries?.length > 0 && (
            <>
              <h2>Gift card ({giftCardProductsInDeliveries?.length ?? 0})</h2>
              {giftCardProductsInDeliveries?.map((giftCardProduct) => (
                <GiftCardProductRow
                  giftCardProduct={giftCardProduct}
                  order={order}
                  currencyCode={order?.currencyCode}
                  refunds={order?.Refunds}
                  refetch={refetch}
                  reason={reason}
                />
              ))}
            </>
          )}
          {shippingFeesInDeliveries?.length > 0 && (
            <>
              <h2>Shipping ({shippingFeesInDeliveries?.length ?? 0})</h2>
              {shippingFeesInDeliveries?.map((shippingFee) => (
                <ShippingRow
                  key={shippingFee?.shippingFeeId}
                  shippingFee={getOrderShippingFee(shippingFee)}
                  order={order}
                  refetch={refetch}
                  reason={reason}
                />
              ))}
            </>
          )}
        </>
      )}
    </>
  )
}
